/**
 * Suggestions Popover Component
 * ===========================
 * 
 * Popover component that displays text suggestions for textarea inputs.
 * This component provides an alternative to native autocomplete by showing
 * previously entered text suggestions with the ability to select or delete them.
 * 
 * Core Features:
 * - Displays list of text suggestions in a popover
 * - Click-to-select functionality for suggestions
 * - Delete individual suggestions with confirmation
 * - Keyboard navigation support with highlighting
 * - Responsive design for mobile and desktop
 * - Automatic popover dismissal when empty
 * 
 * Input Properties:
 * - suggestions: Array of text suggestions to display
 * - onDelete: Callback function for deleting suggestions
 * 
 * User Interaction:
 * - Click suggestion to select and close popover
 * - Hover over suggestions to highlight them
 * - Click delete button to remove individual suggestions
 * - Automatic popover closure when all suggestions are deleted
 * 
 * Styling Features:
 * - Custom popover styling with Ionic components
 * - Responsive design adjustments for mobile devices
 * - Highlighted state for selected suggestions
 * - Consistent spacing and typography
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input } from '@angular/core';
import { PopoverController } from '@ionic/angular';

@Component({
  selector: 'app-suggestions-popover',
  template: `
  <ion-content>
    <ion-list>
      <ion-item 
        *ngFor="let suggestion of suggestions; let i = index"
        button
        detail="false"
        lines="none"
        [class.selected]="highlightedIndex === i"
        (click)="selectSuggestion(suggestion)"
        (mouseenter)="highlightedIndex = i">
        <ion-label class="ion-text-wrap">
          {{ suggestion | slice:0:100 }}{{ suggestion.length > 100 ? '...' : '' }}
        </ion-label>
        <ion-button 
          slot="end" 
          fill="clear"
          color="medium"
          (click)="deleteSuggestion(suggestion, $event)">
          <ion-icon name="close" slot="icon-only"></ion-icon>
        </ion-button>
      </ion-item>
      
    </ion-list>
  </ion-content>
`,
  styles: [`
    ion-content {
      --background: var(--ion-background-color);
    }
    
    ion-header {
      ion-toolbar {
        --min-height: 44px;
        --background: var(--ion-background-color);
        --border-width: 0 0 1px 0;
        --border-color: var(--ion-border-color);
        
        ion-title {
          font-size: 16px;
          font-weight: 600;
        }
      }
    }
    
    ion-list {
      background: transparent;
      padding: 0;
    }
    
    ion-item {
      --padding-start: 16px;
      --padding-end: 8px;
      --min-height: 44px;
      
      &.selected {
        --background: var(--ion-color-light);
      }
      
      ion-label {
        margin: 8px 0;
        font-size: 14px;
      }
      
      ion-button {
        --padding-start: 8px;
        --padding-end: 8px;
        margin: 0;
        height: 32px;
        
        ion-icon {
          font-size: 20px;
        }
      }
    }


    
    /* Mobile adjustments */
    @media (max-width: 768px) {
      ion-item {
        --min-height: 48px;
        
        ion-label {
          font-size: 16px;
        }
      }
    }
  `]
})
export class SuggestionsPopoverComponent {
  @Input() suggestions: string[] = [];
  @Input() onDelete?: (suggestion: string) => void;
  
  highlightedIndex: number = -1;

  constructor(private popoverController: PopoverController) {}

  /**
   * Selects a suggestion and dismisses the popover.
   * Passes the selected suggestion back to the parent component
   * through the popover dismiss data.
   * 
   * @param suggestion - The text suggestion that was selected
   */
  selectSuggestion(suggestion: string) {
    this.popoverController.dismiss({
      selected: suggestion
    });
  }

  /**
   * Deletes a suggestion from the list.
   * Prevents event propagation to avoid triggering selection.
   * Calls the parent's delete handler and updates the local suggestions array.
   * Automatically closes the popover if no suggestions remain.
   * 
   * @param suggestion - The suggestion to delete
   * @param event - The click event object
   */
  deleteSuggestion(suggestion: string, event: Event) {
    event.stopPropagation();
    
    // Call parent's delete handler
    if (this.onDelete) {
      this.onDelete(suggestion);
    }
    
    // Remove from local array
    this.suggestions = this.suggestions.filter(s => s !== suggestion);
    
    // If no more suggestions, close popover
    if (this.suggestions.length === 0) {
      this.dismiss();
    }
  }

  /**
   * Dismisses the popover without selecting any suggestion.
   * Used when the popover should be closed programmatically.
   */
  dismiss() {
    this.popoverController.dismiss();
  }
}